/* ==========================================================================
   <media-gallery>
   Vertical thumb rail + main image swap on click. Cole Buxton style.
   ========================================================================== */

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.thumbs = this.querySelectorAll('[data-media-thumb]');
    this.main   = this.querySelector('[data-media-main]');
    this.track  = this.querySelector('[data-media-track]');
    if (!this.thumbs.length || !this.main) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.activate(thumb);
      });
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.activate(thumb);
        }
      });
    });

    this._onVariantChange = this._onVariantChange || this._handleVariantChange.bind(this);
    document.addEventListener('variant:change', this._onVariantChange);

    // Swipe between images on touch devices (next/prev). Reuses activate().
    // Only a clearly horizontal gesture counts. A mostly vertical move is the
    // user scrolling the page, not swiping the image.
    let touchStartX = null;
    let touchStartY = null;
    this.main.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });
    this.main.addEventListener('touchend', (e) => {
      if (touchStartX === null) return;
      const dx = e.changedTouches[0].screenX - touchStartX;
      const dy = e.changedTouches[0].screenY - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(dx) < 40) return; // Ignore taps and micro-drags.
      if (Math.abs(dx) <= Math.abs(dy)) return; // Vertical scroll, not a swipe.
      this.step(dx < 0 ? 1 : -1);
    }, { passive: true });

    // Mobile slide track. Swiping it is a native scroll, so all this has to do
    // is keep the active-media bookkeeping honest once the scroll settles.
    if (this.track) {
      // The timer lives on the instance so _scrollToMedia can cancel a settle
      // that is still pending from the user's own swipe — otherwise it fires
      // mid-animation, reads a scrollLeft in transit and activates the wrong
      // slide.
      this.track.addEventListener('scroll', () => {
        if (this._scrollingToSlide) return;
        clearTimeout(this._settle);
        this._settle = setTimeout(() => this._syncFromTrack(), 120);
      }, { passive: true });
    }

    // Zoom on hover (cursor-tracked transform-origin)
    if (this.dataset.zoom === 'true') {
      this.main.addEventListener('mousemove', (e) => {
        const rect = this.main.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        this.main.style.setProperty('--zoom-x', `${x}%`);
        this.main.style.setProperty('--zoom-y', `${y}%`);
      });
      this.main.addEventListener('mouseleave', () => {
        this.main.style.removeProperty('--zoom-x');
        this.main.style.removeProperty('--zoom-y');
      });
    }

    requestAnimationFrame(() => { this._ready = true; });
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange);
    clearTimeout(this._settle);
    clearTimeout(this._scrollGuard);
  }

  _handleVariantChange(event) {
    const mediaId = event.detail?.variant?.featured_media?.id;
    if (!mediaId) return;
    const targetThumb = this.querySelector(`[data-media-thumb][data-media-id="${mediaId}"]`);
    if (targetThumb) this.activate(targetThumb);
  }

  step(dir) {
    const list = Array.from(this.thumbs);
    if (list.length < 2) return;
    let i = list.findIndex((t) => t.hasAttribute('data-active'));
    if (i === -1) i = 0;
    const next = (i + dir + list.length) % list.length;
    this.activate(list[next]);
  }

  // Scroll settled — mark the slide nearest the track's left edge as active.
  _syncFromTrack() {
    const slides = Array.from(this.track.querySelectorAll('[data-media-slide]'));
    if (!slides.length) return;
    const left = this.track.scrollLeft;
    let nearest = slides[0];
    let best = Infinity;
    slides.forEach((slide) => {
      const delta = Math.abs(slide.offsetLeft - left);
      if (delta < best) { best = delta; nearest = slide; }
    });
    const id = nearest.getAttribute('data-media-id');
    const thumb = this.querySelector(`[data-media-thumb][data-media-id="${id}"]`);
    if (thumb && !thumb.hasAttribute('data-active')) this.activate(thumb, { scroll: false });
  }

  // Bring the matching slide into view — only when the track is the visible
  // gallery (mobile). On desktop it is display:none and has no layout.
  _scrollToMedia(id) {
    if (!this.track || !this.track.offsetParent) return;
    const slide = this.track.querySelector(`[data-media-slide][data-media-id="${id}"]`);
    if (!slide) return;
    this._scrollingToSlide = true;
    clearTimeout(this._settle);
    // The first sync happens on load (variant-picker announces the preselected
    // variant) — jump there rather than animating the page in front of the user.
    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: this._ready ? 'smooth' : 'auto',
    });
    clearTimeout(this._scrollGuard);
    this._scrollGuard = setTimeout(() => { this._scrollingToSlide = false; }, 400);
  }

  activate(thumb, { scroll = true } = {}) {
    const id = thumb.getAttribute('data-media-id');
    const src = thumb.getAttribute('data-media-src');
    const alt = thumb.getAttribute('data-media-alt') || '';
    const srcset = thumb.getAttribute('data-media-srcset') || '';

    this.thumbs.forEach((t) => t.removeAttribute('data-active'));
    thumb.setAttribute('data-active', 'true');

    // Below 750px the track is the visible gallery and .pdp__main-media is
    // display:none. Swapping its src there downloads a full-size photo nobody
    // sees — and a different one than the track already fetched, because the
    // two carry different srcset candidates. Measured: three swipes cost four
    // redundant requests, 163KB, on mobile data.
    const img = this.main.querySelector('img');
    const mainIsVisible = !(this.track && this.track.offsetParent);
    if (img && src && mainIsVisible) {
      img.src = src;
      if (srcset) img.srcset = srcset;
      img.alt = alt;
    }
    this.main.setAttribute('data-active-media', id);
    if (scroll) this._scrollToMedia(id);
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
